import { CancellationToken, IHttpApi, IHttpApiFetchConfig, IModelResponse } from '@cian/http-api';
import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { HttpBadStatusCodeError } from '@cian/peperrors/shared';
import { TMetaDeepJson } from '@cian/logger/shared';

import { UNKNOWN_REQUEST_REJECTION } from '../constants/api';
import { type IAppContext } from '../types/applicationContext';
import { extractStoreStateForRequestLog } from '../utils/extractStoreStateForRequestLog';

export const createBaseQuery: TCreateBaseQueryFn = ({ httpApi, logger }) => {
  return async ({ fetchFunction, parameters, loggerInfo, config }, baseQueryApi) => {
    const domain = baseQueryApi?.endpoint ?? 'unknown-request';

    const cancellationToken = new CancellationToken();
    const cancelRequestCallback = () => cancellationToken.cancel();
    baseQueryApi?.signal.addEventListener('abort', cancelRequestCallback);

    try {
      const { statusCode, response } = await fetchFunction({
        httpApi,
        parameters,
        config: { ...config, cancellationToken },
      });

      if (statusCode === 200) {
        return { data: response };
      }

      logger[statusCode === 400 ? 'warning' : 'error'](
        new HttpBadStatusCodeError({
          message: `Запрос ${domain} ответил с кодом ${statusCode}`,
          domain,
          statusCode,
        }),
        {
          description: loggerInfo.description,
          degradation: loggerInfo.degradation,
          parameters: JSON.stringify(loggerInfo.safeParameters),
          storeState: extractStoreStateForRequestLog(baseQueryApi?.getState()),
          response: JSON.stringify(response),
        },
      );

      return { error: response };
    } catch (error) {
      logger.error(error as Error, {
        domain,
        description: loggerInfo.description,
        degradation: loggerInfo.degradation,
        parameters: JSON.stringify(loggerInfo.safeParameters),
        storeState: extractStoreStateForRequestLog(baseQueryApi?.getState()),
      });

      return { error: UNKNOWN_REQUEST_REJECTION };
    } finally {
      baseQueryApi?.signal.removeEventListener('abort', cancelRequestCallback);
    }
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TParameters = any;
type TCreateBaseQueryFn = (context: IAppContext) => BaseQueryFn<{
  fetchFunction: (params: {
    httpApi: IHttpApi;
    parameters: TParameters;
    config: IHttpApiFetchConfig;
  }) => Promise<IModelResponse<unknown>>;
  /** Параметры запроса */
  parameters?: TParameters;
  /** Конфиг запроса */
  config?: IHttpApiFetchConfig;
  /** Данные для логирования в случае ошибки */
  loggerInfo: {
    /** Описание запроса на русском */
    description: string;
    /** Описание деградации на русском */
    degradation?: string;
    /** Параметры запроса без чувствительных данных */
    safeParameters?: TMetaDeepJson;
  };
}>;
